export { FooterConfigFieldRender } from './puck-footer-config-field'
export { HeaderConfigFieldRender } from './puck-header-config-field'
export { ImageFieldRender, ImagePickerField } from './puck-image-picker'

/**
 * Custom Puck field renderers for color, image, video, and boolean types.
 * Used by the puck-schema-generator to render rich field editors.
 */

// ============================================
// COLOR FIELD
// ============================================

interface ColorFieldProps {
  value: unknown
  onChange: (value: unknown) => void
  name?: string
}

/**
 * Color picker field with hex text input.
 * Renders a native color input alongside a text input for the hex value.
 */
export function ColorFieldRender({ value, onChange, name }: ColorFieldProps) {
  const colorValue = typeof value === 'string' ? value : '#000000'

  // Convert camelCase field key to readable label (e.g. "headingColor" → "Heading Color")
  const displayLabel = name
    ? name
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, s => s.toUpperCase())
        .trim()
    : ''

  return (
    <div>
      {displayLabel && (
        <div style={{ fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>{displayLabel}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
        <input
          type="color"
          value={colorValue || '#000000'}
          onChange={e => onChange(e.target.value)}
          style={{
            width: '36px',
            height: '36px',
            padding: '2px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            backgroundColor: 'transparent',
          }}
        />
        <input
          type="text"
          value={colorValue || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="#000000"
          style={{
            flex: 1,
            padding: '6px 10px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            fontFamily: 'monospace',
            outline: 'none',
          }}
        />
      </div>
    </div>
  )
}

// ============================================
// VIDEO FIELD
// ============================================

interface VideoFieldProps {
  value: unknown
  onChange: (value: unknown) => void
}

/**
 * Video URL field.
 * Renders a simple text input for the video URL.
 */
export function VideoFieldRender({ value, onChange }: VideoFieldProps) {
  const videoUrl = typeof value === 'string' ? value : ''

  return (
    <input
      type="text"
      value={videoUrl}
      onChange={e => onChange(e.target.value)}
      placeholder="Enter video URL..."
      style={{
        width: '100%',
        padding: '6px 10px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        fontSize: '14px',
        outline: 'none',
        boxSizing: 'border-box',
      }}
    />
  )
}

// ============================================
// BOOLEAN FIELD
// ============================================

interface BooleanFieldProps {
  value: unknown
  onChange: (value: unknown) => void
  name?: string
}

/**
 * Boolean toggle field.
 * Renders a checkbox-style toggle with a label.
 */
export function BooleanFieldRender({ value, onChange, name }: BooleanFieldProps) {
  const checked = value === true || value === 'true'

  // Convert camelCase field key to readable label (e.g. "autoplay" → "Autoplay")
  const displayLabel = name
    ? name
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, s => s.toUpperCase())
        .trim()
    : ''

  return (
    <div>
      {displayLabel && (
        <div style={{ fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>{displayLabel}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          type="button"
          onClick={() => onChange(!checked)}
          style={{
            width: '40px',
            height: '22px',
            borderRadius: '11px',
            border: 'none',
            backgroundColor: checked ? '#16a34a' : '#d1d5db',
            cursor: 'pointer',
            position: 'relative',
            transition: 'background-color 0.2s ease',
            padding: 0,
          }}
        >
          <span
            style={{
              display: 'block',
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              backgroundColor: '#fff',
              position: 'absolute',
              top: '2px',
              left: checked ? '20px' : '2px',
              transition: 'left 0.2s ease',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          />
        </button>
        <span style={{ fontSize: '13px', color: '#6b7280' }}>{checked ? 'Yes' : 'No'}</span>
      </div>
    </div>
  )
}
